import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal component for smooth scroll-driven entrance animations.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {'up' | 'down' | 'left' | 'right' | 'scale' | 'fade'} [props.variant='up']
 * @param {number} [props.delay=0] - Delay in milliseconds
 * @param {number} [props.duration=700] - Duration in milliseconds
 * @param {string} [props.className=''] - Extra classes
 * @param {number} [props.threshold=0.15] - Intersection observer threshold
 * @param {boolean} [props.once=true] - Trigger animation only once
 */
export default function ScrollReveal({
  children,
  variant = 'up',
  delay = 0,
  duration = 700,
  className = '',
  threshold = 0.12,
  once = true,
  ...rest
}) {
  const [isRevealed, setIsRevealed] = useState(false);
  const domRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRevealed(true);
            if (once && domRef.current) {
              observer.unobserve(domRef.current);
            }
          } else if (!once) {
            setIsRevealed(false);
          }
        });
      },
      {
        threshold,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    const currentRef = domRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, once]);

  // Variant transformations before reveal
  const getInitialTransform = () => {
    switch (variant) {
      case 'up':
        return 'translate3d(0, 36px, 0)';
      case 'down':
        return 'translate3d(0, -36px, 0)';
      case 'left':
        return 'translate3d(36px, 0, 0)';
      case 'right':
        return 'translate3d(-36px, 0, 0)';
      case 'scale':
        return 'scale(0.92) translate3d(0, 20px, 0)';
      case 'fade':
      default:
        return 'translate3d(0, 0, 0)';
    }
  };

  const style = {
    opacity: isRevealed ? 1 : 0,
    transform: isRevealed ? 'translate3d(0,0,0) scale(1)' : getInitialTransform(),
    transitionProperty: 'opacity, transform',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    transitionDelay: `${delay}ms`,
    willChange: 'opacity, transform',
  };

  return (
    <div ref={domRef} style={style} className={className} {...rest}>
      {children}
    </div>
  );
}
